import classNames from 'classnames';
import { ComponentProps, ReactNode } from 'react';
import { Size } from '../../types';
import Checkbox from '../Checkbox';
import CheckboxRadioLabel from '../CheckboxRadioLabel/CheckboxRadioLabel';
import InputField from '../InputField';
import styles from './CheckboxGroup.module.scss';

type Props = {
  checkboxClassName?: string;
  className?: string;
  disabled?: boolean;
  errorText?: ComponentProps<typeof InputField>['errorText'];
  helperText?: ComponentProps<typeof InputField>['helperText'];
  labelText?: ComponentProps<typeof InputField>['labelText'];
  labelTextVariant?: ComponentProps<typeof InputField>['labelTextVariant'];
  legendText?: ReactNode;
  onChange: (id: string, selected: boolean) => void;
  options: {
    disabled?: ComponentProps<typeof Checkbox>['disabled'];
    id: string;
    selected: ComponentProps<typeof Checkbox>['selected'];
    subtext?: ReactNode;
    testId?: string;
    text: ReactNode;
  }[];
  size?: Size<'sm' | 'md'>;
  testId?: string;
  verticalAlignment?: 'center' | 'top';
};

// https://www.figma.com/design/9Y1n3VoMJAcTGPe82bvySj/Denali-Components?node-id=12-8314&m=dev
const CheckboxGroup = ({
  checkboxClassName,
  className,
  disabled: groupDisabled,
  errorText,
  helperText,
  labelText,
  labelTextVariant,
  legendText,
  onChange,
  options,
  size = 'md',
  testId,
  verticalAlignment
}: Props) => {
  if (legendText) {
    return (
      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>{legendText}</legend>
        {options.map(option => {
          const { subtext, text, disabled, testId: optionTestId, selected, id } = option;

          return (
            <Checkbox
              key={id}
              className={checkboxClassName}
              disabled={groupDisabled || disabled}
              id={id}
              labelElement={<CheckboxRadioLabel text={text} subtext={subtext} size={size} disabled={groupDisabled || disabled} />}
              onChange={checked => {
                onChange(id, Boolean(checked));
              }}
              selected={selected}
              size={size}
              testId={optionTestId}
              verticalAlignment={verticalAlignment}
            />
          );
        })}
      </fieldset>
    );
  }

  return (
    <InputField
      className={classNames(styles.container, className)}
      disabled={groupDisabled}
      errorText={errorText}
      helperText={helperText}
      labelText={labelText}
      labelTextVariant={labelTextVariant}
      role="group"
      testId={testId}
      useDivInsteadOfLabel
    >
      {options.map(option => {
        const { subtext, text, disabled, testId: optionTestId, selected, id } = option;

        return (
          <Checkbox
            key={id}
            className={checkboxClassName}
            disabled={groupDisabled || disabled}
            id={id}
            labelElement={<CheckboxRadioLabel text={text} subtext={subtext} size={size} disabled={groupDisabled || disabled} />}
            onChange={checked => {
              onChange(id, Boolean(checked));
            }}
            selected={selected}
            size={size}
            testId={optionTestId}
            verticalAlignment={verticalAlignment}
          />
        );
      })}
    </InputField>
  );
};

export default CheckboxGroup;
